import React from 'react';
import { Form, useActionData, useNavigate, useNavigation, useRevalidator } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';

import {
  Action,
  Resource,
  type UpdateWordDocumentTemplateMutation,
  type WordDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { PermissionNotice, usePermissions } from '~/components/permissions';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

import { SectionedTemplateFields } from './word-template-section-fields';
import {
  appendSectionsToFormData,
  type WordTemplateFormState,
  type WordTemplateSectionFormEntry
} from '~/components/library/lib/word-template-sections';
import { OfficeOnlineFilePreview } from '~/components/office-online-embed-url';

const UPDATE_WORD_TEMPLATE = [{ resource: Resource.WordDocumentTemplate, action: Action.Update }];
const CREATE_WORD_TEMPLATE = [{ resource: Resource.WordDocumentTemplate, action: Action.Create }];

const WORD_EXTENSION_RE = /\.docx$/i;
const POST_SAVE_CLOSE_DELAY_MS = 1000;

/** Map a server-loaded template to the editor's form state. Each section
 *  keeps its existing `templateFileId` pinned (so an untouched submit
 *  preserves the stored file) and carries an `existingFile` metadata
 *  bundle that powers the Replace-file UX in the section card. */
const formStateFromTemplate = (
  template: WordDocumentTemplateFieldsFragment,
  visibility: VisibilitySelectorValue | undefined
): WordTemplateFormState => ({
  title: template.title || '',
  description: template.description || '',
  fillGuidance: template.fillGuidance ?? '',
  visibility: visibility ? visibilityInputFromValue(visibility) : undefined,
  sections: template.sections.map(
    (s): WordTemplateSectionFormEntry => ({
      id: s.id,
      title: s.title,
      setupGuidance: s.setupGuidance ?? null,
      static: s.static,
      repeatable: s.repeatable,
      required: s.required,
      uploadTemplate: null,
      templateFileId: s.templateFile.id,
      existingFile: {
        id: s.templateFile.id,
        name: s.templateFile.name,
        url: s.templateFile.url ?? null
      }
    })
  )
});

/**
 * Edit slideout for Word document templates. Renders the same sectioned
 * form used by the create dialog (title / description / fill guidance /
 * sections + per-section file replace) plus a DOCX preview on the right.
 *
 * Submission is delegated to `props.onSubmit` so the parent route can
 * choose how to navigate after the update completes. The component itself
 * builds a `multipart/form-data` payload identical to the one the create
 * dialog ships (sections JSON + per-index file uploads), which the shared
 * `updateWordDocumentTemplate` route action knows how to parse.
 */
export function WordTemplateEditor(props: {
  wordTemplate: WordDocumentTemplateFieldsFragment;
  validationErrors: ValidationError[];
  /** URL of the list page to navigate back to on close. */
  listUrl: string;
  onSubmit: (formData: FormData) => void;
  /** Opens the create dialog prefilled from this template (duplicate flow). */
  onDuplicate?: () => void;
}) {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const action = useActionData() as ActionResult<UpdateWordDocumentTemplateMutation>;

  const visibilityEditAllowed = useVisibilityEditAllowed(props.wordTemplate.createdBy?.id);
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(
    visibilityValueFromFragment(props.wordTemplate.visibility)
  );

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<WordTemplateFormState>(() =>
    formStateFromTemplate(
      props.wordTemplate,
      visibilityValueFromFragment(props.wordTemplate.visibility)
    )
  );
  const { updateForm, getValidationError } = useFormActions<WordTemplateFormState>(
    setForm,
    setValidationErrors
  );
  const [error, setError] = React.useState<string>();
  const [closingAfterSave, setClosingAfterSave] = React.useState(false);
  const handledSaveKeyRef = React.useRef<string | null>(null);

  // Re-seed local state when the route swaps to a different word template
  // (or the parent reloads after a save). Edits on the same template are
  // intentionally preserved by checking that the id changed.
  const lastTemplateIdRef = React.useRef(props.wordTemplate.id);
  React.useEffect(() => {
    if (lastTemplateIdRef.current === props.wordTemplate.id) return;
    lastTemplateIdRef.current = props.wordTemplate.id;
    const nextVisibility = visibilityValueFromFragment(props.wordTemplate.visibility);
    setVisibility(nextVisibility);
    setForm(formStateFromTemplate(props.wordTemplate, nextVisibility));
    setValidationErrors([]);
    setError(undefined);
  }, [props.wordTemplate]);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  // After a successful update, keep the loading state briefly so ingestion can
  // transition on the server, revalidate the library list, then close the slideout.
  React.useEffect(() => {
    const updated = action?.data?.updateWordDocumentTemplate;
    if (!updated?.id || action?.validationErrors?.length) {
      return;
    }
    const saveKey = `${updated.id}:${updated.updatedAt}`;
    if (handledSaveKeyRef.current === saveKey) {
      return;
    }
    handledSaveKeyRef.current = saveKey;
    setClosingAfterSave(true);
    const timer = window.setTimeout(() => {
      revalidator.revalidate();
      navigate(props.listUrl);
    }, POST_SAVE_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    action?.data?.updateWordDocumentTemplate,
    action?.validationErrors,
    navigate,
    props.listUrl,
    revalidator
  ]);

  React.useEffect(() => {
    handledSaveKeyRef.current = null;
    setClosingAfterSave(false);
  }, [props.wordTemplate.id]);

  const onClose = () => {
    navigate(props.listUrl);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Editor always sends sections — empty sections would wipe the template
    // structure server-side, so block that up front rather than letting the
    // request go through.
    if (form.sections.length === 0) {
      setError('Add at least one section before saving.');
      return;
    }

    const badExtension = form.sections
      .map((s) => s.uploadTemplate)
      .find((f): f is File => !!f && !WORD_EXTENSION_RE.test(f.name));
    if (badExtension) {
      setError('Upload a .docx file only, not .doc, .pdf, .pages, .dotx, or other formats.');
      return;
    }
    setError(undefined);

    const formData = new FormData();
    formData.set('title', form.title);
    formData.set('description', form.description);
    formData.set('fillGuidance', form.fillGuidance ?? '');
    if (visibilityEditAllowed) {
      formData.set('visibility', JSON.stringify(visibilityInputFromValue(visibility)));
    }
    appendSectionsToFormData(formData, form.sections);

    props.onSubmit(formData);
  };

  const permissions = usePermissions();
  const canEdit = permissions.hasAllPermissions(UPDATE_WORD_TEMPLATE);
  const canDuplicate = permissions.hasAllPermissions(CREATE_WORD_TEMPLATE);
  const isSubmitting =
    navigation.state === 'submitting' || navigation.state === 'loading' || closingAfterSave;
  // The deprecated top-level templateFile resolver serves the first section's
  // file. For multi-section templates this is somewhat arbitrary, but keeping
  // it preserves parity with the previous editor and gives the user a quick
  // glance at the first section. A more ambitious editor would let the user
  // pick which section to preview; out of scope for this pass.
  const previewUrl = props.wordTemplate.templateFile?.url ?? null;

  return (
    <MountableSlideout
      onClose={onClose}
      title={canEdit ? 'Edit word template' : 'View word template'}
      size="7xl"
    >
      {!canEdit && (
        <PermissionNotice className="mb-6">
          You can view this word template but not change it.
        </PermissionNotice>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-8 min-w-0 w-full items-stretch">
        <Form
          onSubmit={handleSubmit}
          className="col-span-1 sm:col-span-2 min-w-0 flex min-h-0 flex-col sm:min-h-[calc(100vh-6rem)]"
          noValidate
        >
          <Fieldset
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain"
            disabled={isSubmitting || !canEdit}
          >
            <FieldGroup className="min-w-0">
              {props.wordTemplate.error && (
                <ErrorMessage>
                  Processing failed: {props.wordTemplate.error}. Fix the document or its guidance
                  and save to try again.
                </ErrorMessage>
              )}

              <Field>
                <Label>Title</Label>
                <Description>
                  A short name shown in your library and in workspace tools.
                </Description>
                <Input
                  className="max-w-xs"
                  value={form.title}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['title'], { exact: true })}
                />
                {!!getValidationError(validationErrors, ['title'], { exact: true }) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['title'], { exact: true })}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Description</Label>
                <Description>What this Word template is for (shown in the library).</Description>
                <Textarea
                  value={form.description}
                  rows={2}
                  resizable={false}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['description'])}
                />
                {!!getValidationError(validationErrors, ['description']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Extra instructions for Cara when this template is listed in workspace tools. Use
                  for context that is not obvious from the .docx alone (placeholders, sections to
                  preserve, naming conventions).
                </Description>
                <AutoResizeTextarea
                  value={form.fillGuidance ?? ''}
                  rows={4}
                  resizable={false}
                  onChange={(e) => {
                    const fillGuidance = e.currentTarget.value;
                    updateForm(['fillGuidance'], (prev) => ({ ...prev, fillGuidance }));
                  }}
                  placeholder="Example: Fill client placeholders from the account legal name. Do not alter the signature block unless the user requests it."
                />
              </Field>

              <SectionedTemplateFields
                form={form}
                updateForm={updateForm}
                getValidationError={(parts) => getValidationError(validationErrors, parts)}
              />

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </FieldGroup>
          </Fieldset>
          <div className="mt-auto flex flex-shrink-0 flex-col items-center gap-3 py-8 *:w-full sm:flex-row sm:*:w-auto">
            <Button type="button" color="white" disabled={isSubmitting} onClick={onClose}>
              Close
            </Button>
            {canDuplicate && props.onDuplicate ? (
              <Button
                type="button"
                color="white"
                disabled={isSubmitting}
                onClick={props.onDuplicate}
              >
                Duplicate
              </Button>
            ) : null}
            {!canEdit ? null : visibilityEditAllowed ? (
              <VisibilitySplitButton
                type="submit"
                value={visibility}
                onChange={setVisibility}
                loading={isSubmitting}
              >
                Update word template
              </VisibilitySplitButton>
            ) : (
              <Button type="submit" color="sky" loading={isSubmitting}>
                Update word template
              </Button>
            )}
          </div>
        </Form>
        <div className="col-span-1 sm:col-span-3 min-w-0">
          {previewUrl && (
            <div className="sticky top-0 flex flex-col h-[calc(100vh-6rem)]">
              <div className="flex-1 min-h-0">
                <OfficeOnlineFilePreview
                  downloadUrl={previewUrl}
                  iframeTitle="Word document preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </MountableSlideout>
  );
}
